/**
 * Servicio de Novedades
 * Maneja operaciones relacionadas con novedades de cheques (rechazos, devoluciones, etc.)
 */
import api from './api';

/**
 * Crear una nueva novedad para un cheque
 * @param {string} chequeId - ID del cheque
 * @param {Object} data - Datos de la novedad (tipo, descripcion, fechaNovedad, etc.)
 * @returns {Promise<Object>} Novedad creada
 */
export const createNovedad = async (chequeId, data) => {
  try {
    const response = await api.post(`/cheques/${chequeId}/novedades`, data);
    return response.data;
  } catch (error) {
    console.error('Error al crear novedad:', error);
    throw error;
  }
};

/**
 * Obtener todas las novedades de un cheque
 * @param {string} chequeId - ID del cheque
 * @returns {Promise<Array>} Lista de novedades del cheque
 */
export const getNovedadesByCheque = async (chequeId) => {
  try {
    const response = await api.get(`/cheques/${chequeId}/novedades`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener novedades del cheque:', error);
    throw error;
  }
};

/**
 * Obtener una novedad por ID
 * @param {string} id - ID de la novedad
 * @returns {Promise<Object>} Datos de la novedad
 */
export const getNovedadById = async (id) => {
  try {
    const response = await api.get(`/novedades/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al obtener novedad:', error);
    throw error;
  }
};

/**
 * Obtener todas las novedades con filtros
 * @param {Object} filters - Filtros (tipo, estado, fechaDesde, fechaHasta, clienteId)
 * @returns {Promise<Array>} Lista de novedades
 */
export const getAllNovedades = async (filters = {}) => {
  try {
    const response = await api.get('/novedades', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener novedades:', error);
    throw error;
  }
};

/**
 * Actualizar una novedad
 * @param {string} id - ID de la novedad
 * @param {Object} data - Datos actualizados
 * @returns {Promise<Object>} Novedad actualizada
 */
export const updateNovedad = async (id, data) => {
  try {
    const response = await api.put(`/novedades/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error al actualizar novedad:', error);
    throw error;
  }
};

/**
 * Resolver una novedad
 * @param {string} id - ID de la novedad
 * @param {Object} resolucion - Datos de resolución (descripcion, fechaResolucion, etc.)
 * @returns {Promise<Object>} Novedad resuelta
 */
export const resolveNovedad = async (id, resolucion) => {
  try {
    const response = await api.patch(`/novedades/${id}/resolver`, resolucion);
    return response.data;
  } catch (error) {
    console.error('Error al resolver novedad:', error);
    throw error;
  }
};

/**
 * Eliminar una novedad
 * @param {string} id - ID de la novedad
 * @returns {Promise<void>}
 */
export const deleteNovedad = async (id) => {
  try {
    const response = await api.delete(`/novedades/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error al eliminar novedad:', error);
    throw error;
  }
};

/**
 * Obtener novedades pendientes
 * @returns {Promise<Array>} Lista de novedades pendientes
 */
export const getNovedadesPendientes = async () => {
  try {
    const response = await api.get('/novedades/pendientes');
    return response.data;
  } catch (error) {
    console.error('Error al obtener novedades pendientes:', error);
    throw error;
  }
};

/**
 * Obtener novedades por tipo
 * @param {string} tipo - Tipo de novedad (RECHAZO, DEVOLUCION, etc.)
 * @returns {Promise<Array>} Lista de novedades del tipo especificado
 */
export const getNovedadesByTipo = async (tipo) => {
  try {
    const response = await api.get('/novedades', {
      params: { tipo },
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener novedades por tipo:', error);
    throw error;
  }
};

/**
 * Obtener estadísticas de novedades
 * @param {Object} filters - Filtros (fechaDesde, fechaHasta)
 * @returns {Promise<Object>} Estadísticas de novedades
 */
export const getNovedadesStats = async (filters = {}) => {
  try {
    const response = await api.get('/novedades/estadisticas', {
      params: filters,
    });
    return response.data;
  } catch (error) {
    console.error('Error al obtener estadísticas de novedades:', error);
    throw error;
  }
};

export default {
  createNovedad,
  getNovedadesByCheque,
  getNovedadById,
  getAllNovedades,
  updateNovedad,
  resolveNovedad,
  deleteNovedad,
  getNovedadesPendientes,
  getNovedadesByTipo,
  getNovedadesStats,
};
